// @flow
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Modal from '../Modal/Modal';
import { useIsMobile, isIOSApp } from '@helpers/helpers';
import { usePdfDocument, usePageSizes } from './PDFjs';
import PDFPage from './PDFPage';
import { useZoomGestures } from './zoomGestures';
import * as Icons from './icons';
import './PDFPreview.scss';

export type PDFDoc = {
  key: string,
  name: string,
  url?: ?string,
  // The URL, when it has to be asked for first (the apps' fresh signed URL). Null: it couldn't be had.
  load?: () => Promise<?string>,
  // The apps' share sheet. Without it, the web's button downloads `url`.
  onDownload?: () => void,
};

type Props = {
  docs?: Array<PDFDoc>,
  startIndex?: number,
  onClose: Function,
  // One document, as the preview used to take it
  docUrl?: ?string,
  pdfName?: string,
};

// The widest a page is laid out on a computer at "fit": a column of text the eye can follow, ~111% of A4
const MAX_FIT_WIDTH = 880;
const MIN_SCALE = 0.3;
const MAX_FIT_ZOOM = 6;
// What the zoom buttons step through, in percent of the page's printed size (pdf.js scale 1 = 133%)
const PERCENT_STEPS = [30, 50, 67, 75, 90, 100, 110, 125, 150, 175, 200, 250, 300, 400, 500, 600];
const percentOf = (scale: number) => Math.round(scale * 75);
// A4 portrait, the size of the placeholder page until the document says its own
const PLACEHOLDER = { width: 595, height: 842 };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type Anchor = { index: number, relX: number, relY: number, x: number, y: number };

const PDFPreview = ({ docs: givenDocs, startIndex = 0, onClose, docUrl, pdfName }: Props) => {
  const { t } = useTranslation();
  const [isPhone] = useIsMobile();
  const docs: Array<PDFDoc> = useMemo(() => givenDocs || (docUrl ? [{ key: docUrl, name: pdfName || '', url: docUrl }] : []),
    [givenDocs, docUrl, pdfName]);
  const [index, setIndex] = useState(clamp(startIndex, 0, Math.max(0, docs.length - 1)));
  const doc = docs[index];
  const [attempt, setAttempt] = useState(0);

  // The document's URL: given, or asked for
  const [resolved, setResolved] = useState<{ key: ?string, url: ?string, failed: boolean }>({ key: null, url: null, failed: false });
  useEffect(() => {
    if (!doc) return undefined;
    const { load } = doc;
    // A retry asks for a fresh URL when it can: the one given may have been a signed URL that expired
    if (!load || (doc.url && attempt === 0)) {
      setResolved({ key: doc.key, url: doc.url, failed: !doc.url });
      return undefined;
    }
    let cancelled = false;
    setResolved({ key: doc.key, url: null, failed: false });
    load().then(
      (url) => { if (!cancelled) setResolved({ key: doc.key, url, failed: !url }); },
      () => { if (!cancelled) setResolved({ key: doc.key, url: null, failed: true }); },
    );
    return () => { cancelled = true; };
  }, [doc, attempt]);

  const url = resolved.key === (doc && doc.key) ? resolved.url : null;
  const { pdfDocument, progress, failed: documentFailed, damaged } = usePdfDocument(url, attempt);
  const failed = documentFailed || (resolved.key === (doc && doc.key) && resolved.failed);
  const sizes = usePageSizes(pdfDocument);

  // ========================== Layout ==========================
  const scrollerRef = useRef(null);
  const pagesRef = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    const measure = () => setBox({ width: scroller.clientWidth, height: scroller.clientHeight });
    measure();
    if (typeof window.ResizeObserver === 'function') {
      const observer = new window.ResizeObserver(measure);
      observer.observe(scroller);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const multiple = docs.length > 1;
  const gutter = isPhone ? 8 : (multiple ? 88 : 32);
  const gap = isPhone ? 8 : 16;
  const padding = isPhone ? 8 : 24;
  const pageSizes = useMemo(() => sizes || [PLACEHOLDER], [sizes]);
  const widest = Math.max(...pageSizes.map(size => size.width));
  const fitScale = Math.max(0.1, (Math.min(Math.max(0, box.width - (2 * gutter)), isPhone ? Infinity : MAX_FIT_WIDTH) || MAX_FIT_WIDTH) / widest);
  // null: fit to the width, which follows the window
  const [zoom, setZoom] = useState<?number>(null);
  const scale = zoom == null ? fitScale : zoom;
  const minScale = Math.min(MIN_SCALE, fitScale);
  const maxScale = fitScale * MAX_FIT_ZOOM;

  const layout = useMemo(() => {
    let top = padding;
    return pageSizes.map((size) => {
      const width = Math.floor(size.width * scale);
      const height = Math.floor(size.height * scale);
      const page = { top, width, height };
      top += height + gap;
      return page;
    });
  }, [pageSizes, scale, padding, gap]);

  // ========================== What's on screen ==========================
  const [view, setView] = useState({ first: 0, last: 0, current: 0 });
  const updateView = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !layout.length) return;
    const { scrollTop, clientHeight, scrollHeight } = scroller;
    // One screen above and below stays drawn, so a scroll never shows an empty page
    const from = scrollTop - clientHeight;
    const to = scrollTop + (2 * clientHeight);
    let first = layout.length - 1;
    let last = 0;
    let current = 0;
    layout.forEach((page, i) => {
      if (page.top + page.height >= from && page.top <= to) {
        first = Math.min(first, i);
        last = Math.max(last, i);
      }
      if (page.top <= scrollTop + (clientHeight / 3)) current = i;
    });
    // Scrolled to the end: the last page, however short (a document that fits the screen stays on its first)
    if (scrollTop > 0 && scrollTop + clientHeight >= scrollHeight - 2) current = layout.length - 1;
    setView(previous => (previous.first === first && previous.last === last && previous.current === current
      ? previous : { first, last, current }));
  }, [layout]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateView();
      });
    };
    updateView();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [updateView]);

  // ========================== Zoom ==========================
  // Where the zoom happened: the point of the page under it stays under it
  const anchorRef = useRef<?Anchor>(null);
  const anchorAt = useCallback((x: number, y: number): ?Anchor => {
    const pages = pagesRef.current;
    if (!pages || !pages.children.length) return null;
    const children = Array.from(pages.children);
    const found = children.findIndex(child => child.getBoundingClientRect().bottom >= y);
    const i = found === -1 ? children.length - 1 : found;
    const rect = children[i].getBoundingClientRect();
    return { index: i, relX: (x - rect.left) / rect.width, relY: (y - rect.top) / rect.height, x, y };
  }, []);

  const center = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return { x: 0, y: 0 };
    const rect = scroller.getBoundingClientRect();
    return { x: rect.left + (rect.width / 2), y: rect.top + (rect.height / 3) };
  }, []);

  // `next`: a scale, or null to fit the width
  const zoomTo = useCallback((next: ?number, at?: { x: number, y: number }) => {
    const target = next == null ? null : clamp(next, minScale, maxScale);
    if (target === zoom) return;
    const sameScale = Math.abs((target == null ? fitScale : target) - scale) < 0.001;
    if (sameScale && target != null) return;
    // The anchor is spent when the scale changes; one left by a zoom that kept the scale would fire on a later resize
    if (!sameScale) {
      const point = at || center();
      anchorRef.current = anchorAt(point.x, point.y);
    }
    setZoom(target);
  }, [anchorAt, center, minScale, maxScale, zoom, scale, fitScale]);

  const zoomIn = useCallback(() => {
    const percent = percentOf(scale);
    const step = PERCENT_STEPS.find(p => p > percent + 1);
    zoomTo(step ? step / 75 : maxScale);
  }, [scale, zoomTo, maxScale]);

  const zoomOut = useCallback(() => {
    const percent = percentOf(scale);
    const step = [...PERCENT_STEPS].reverse().find(p => p < percent - 1);
    zoomTo(step ? step / 75 : minScale);
  }, [scale, zoomTo, minScale]);

  const fit = useCallback(() => zoomTo(null), [zoomTo]);

  useLayoutEffect(() => {
    const pages = pagesRef.current;
    if (pages) pages.style.transform = '';
    const anchor = anchorRef.current;
    const scroller = scrollerRef.current;
    anchorRef.current = null;
    if (!anchor || !scroller || !pages || !pages.children[anchor.index]) return;
    const rect = pages.children[anchor.index].getBoundingClientRect();
    scroller.scrollLeft += (rect.left + (anchor.relX * rect.width)) - anchor.x;
    scroller.scrollTop += (rect.top + (anchor.relY * rect.height)) - anchor.y;
  }, [scale]);

  useZoomGestures(scrollerRef, pagesRef, {
    onZoomBy: (factor, at) => zoomTo(scale * factor, at),
    clampPinch: factor => clamp(scale * factor, minScale, maxScale) / scale,
    onPinchEnd: (factor, from, to) => {
      // The pinch's transform is still on the pages: what the fingers held is under `to` now
      const anchor = anchorAt(to.x, to.y);
      const next = clamp(scale * factor, minScale, maxScale);
      if (Math.abs(next - scale) < 0.001) {
        if (pagesRef.current) pagesRef.current.style.transform = '';
        return;
      }
      anchorRef.current = anchor && { ...anchor, x: to.x, y: to.y };
      setZoom(next);
    },
    // Fit ↔ 2.5×, around the tap
    onDoubleTap: at => zoomTo(scale > fitScale * 1.05 ? null : fitScale * 2.5, at),
  });

  // ========================== Documents ==========================
  const goTo = useCallback((next: number) => {
    if (next < 0 || next >= docs.length || next === index) return;
    setIndex(next);
    setZoom(null);
    setAttempt(0);
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.scrollTop = 0;
      scroller.scrollLeft = 0;
    }
  }, [docs.length, index]);

  const retry = useCallback(() => setAttempt(n => n + 1), []);

  // A click on a button or link leaves the focus on the pages, where ↑ ↓ and the space bar scroll them
  const keepFocusOnPages = useCallback((e: Object) => {
    if (e.target && e.target.closest && e.target.closest('button, a')) e.preventDefault();
  }, []);

  // ========================== Pages ==========================
  const pageStart = useCallback((page: number) => (page <= 0 ? 0 : layout[page].top - padding), [layout, padding]);

  const scrollToPage = useCallback((page: number) => {
    const scroller = scrollerRef.current;
    if (!scroller || !layout.length) return;
    scroller.scrollTop = pageStart(clamp(page, 0, layout.length - 1));
    updateView();
  }, [layout, pageStart, updateView]);

  const nextPage = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    if (view.current < layout.length - 1) {
      scrollToPage(view.current + 1);
    } else {
      scroller.scrollTop = scroller.scrollHeight;
      updateView();
    }
  }, [view, layout, scrollToPage, updateView]);

  // From the middle of a page, its start comes before the previous page
  const previousPage = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !layout.length) return;
    scrollToPage(scroller.scrollTop > pageStart(view.current) + 1 ? view.current : view.current - 1);
  }, [view, layout, pageStart, scrollToPage]);

  // The page counter turns into a field to type the page in
  const [typedPage, setTypedPage] = useState<?string>(null);
  const editPage = useCallback(() => setTypedPage(String(view.current + 1)), [view]);
  const stopEditingPage = useCallback(() => {
    setTypedPage(null);
    if (scrollerRef.current) scrollerRef.current.focus({ preventScroll: true });
  }, []);
  const submitPage = useCallback((e: Object) => {
    e.preventDefault();
    const page = parseInt(typedPage, 10);
    if (page) scrollToPage(page - 1);
    stopEditingPage();
  }, [typedPage, scrollToPage, stopEditingPage]);
  // The modal closes on Escape's keyup: this one only puts the counter back
  const onPageKeyUp = useCallback((e: Object) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    stopEditingPage();
  }, [stopEditingPage]);
  useEffect(() => setTypedPage(null), [index]);
  const focusPageInput = useCallback((input: ?Object) => {
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKeyDown = (e: Object) => {
      const { target } = e;
      const typing = !!target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)
        || (!!target.closest && !!target.closest('[contenteditable]:not([contenteditable="false"])')));
      if (e.defaultPrevented || typing) return;
      const scroller = scrollerRef.current;
      const pansSideways = !!scroller && scroller.scrollWidth > scroller.clientWidth + 1;
      const command = e.ctrlKey || e.metaKey;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0' && command) {
        e.preventDefault();
        fit();
      } else if (!command && !e.altKey && !pansSideways && e.key === 'ArrowLeft') {
        goTo(index - 1);
      } else if (!command && !e.altKey && !pansSideways && e.key === 'ArrowRight') {
        goTo(index + 1);
      } else if (!command && !e.altKey && e.key === 'PageDown') {
        e.preventDefault();
        nextPage();
      } else if (!command && !e.altKey && e.key === 'PageUp') {
        e.preventDefault();
        previousPage();
      } else if (!command && !e.altKey && e.key === 'Home') {
        e.preventDefault();
        scrollToPage(0);
      } else if (!command && !e.altKey && e.key === 'End') {
        e.preventDefault();
        scrollToPage(layout.length - 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoomIn, zoomOut, fit, goTo, index, nextPage, previousPage, scrollToPage, layout.length]);

  // ========================== Render ==========================
  if (!doc) return null;
  const pageCount = pdfDocument ? pdfDocument.numPages : 0;
  const loading = !pdfDocument && !failed;
  let pageText = t('timeline:pdf/loading');
  if (pageCount) pageText = t('timeline:pdf/page_of', { page: view.current + 1, pages: pageCount });
  else if (failed) pageText = '';
  let pageCounter = pageText;
  if (pageCount > 1 && typedPage != null) {
    pageCounter = (
      <form className="pdfPageJump" onSubmit={submitPage}>
        <span>{t('timeline:pdf/page')}</span>
        <input
          ref={focusPageInput}
          className="pdfPageInput"
          type="text"
          inputMode="numeric"
          aria-label={t('timeline:pdf/go_to_page')}
          value={typedPage}
          size={String(pageCount).length + 1}
          onChange={e => setTypedPage(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onKeyUp={onPageKeyUp}
          onBlur={() => setTypedPage(null)}
        />
        <span>{t('timeline:pdf/of_pages', { pages: pageCount })}</span>
      </form>
    );
  } else if (pageCount > 1) {
    pageCounter = (
      <button type="button" className="pdfPageButton" onClick={editPage} title={t('timeline:pdf/go_to_page_hint')}>
        {pageText}
      </button>
    );
  }
  const fileText = t('timeline:pdf/file_of', { file: index + 1, files: docs.length });
  const prev = docs[index - 1];
  const next = docs[index + 1];
  // isCordovaIos: the iOS build's index.html (and the force-ios test switch), as userNotifications.js has it
  const ShareIcon = window.isCordovaIos || isIOSApp() ? Icons.ShareIos : Icons.ShareAndroid;

  const downloadButton = doc.onDownload ? (
    <button type="button" className="pdfButton" onClick={doc.onDownload} aria-label={t('timeline:pdf/share')} title={t('timeline:pdf/share')}>
      {window.isCordovaApp ? <ShareIcon /> : <Icons.Download />}
      {!isPhone && <span className="pdfButtonText">{t(window.isCordovaApp ? 'timeline:pdf/share' : 'timeline:pdf/download')}</span>}
    </button>
  ) : (url && (
    <a className="pdfButton" href={url} download={doc.name} target="_blank" rel="noopener noreferrer" title={t('timeline:pdf/download')}>
      <Icons.Download />
      {!isPhone && <span className="pdfButtonText">{t('timeline:pdf/download')}</span>}
    </a>
  ));

  return (
    <Modal onClose={onClose} className={classNames('pdfPreview pdfViewer', { isPhone, isDesktop: !isPhone })} throughPortal>
      <div className="pdfToolbar" onMouseDown={keepFocusOnPages}>
        {isPhone && (
          <button type="button" className="pdfButton pdfClose" onClick={onClose} aria-label={t('timeline:pdf/close')}>
            <Icons.Close />
          </button>
        )}
        <div className="pdfTitle">
          {!isPhone && <span className="pdfFileIcon"><Icons.File /></span>}
          <div className="pdfTitleText">
            <span className="pdfName" title={doc.name}>{doc.name}</span>
            <span className="pdfMeta">
              {pageCounter}
              {multiple && !isPhone && <span className={classNames('pdfMetaFile', { alone: !pageCounter })}>{fileText}</span>}
            </span>
          </div>
        </div>
        {!isPhone && (
          <div className="pdfZoom" role="group" aria-label={t('timeline:pdf/zoom')}>
            <button type="button" className="pdfButton" onClick={zoomOut} disabled={!pdfDocument || scale <= minScale + 0.001} aria-label={t('timeline:pdf/zoom_out')} title={t('timeline:pdf/zoom_out')}>
              <Icons.Minus />
            </button>
            <span className="pdfZoomValue" aria-live="polite">{pdfDocument ? `${percentOf(scale)}%` : ''}</span>
            <button type="button" className="pdfButton" onClick={zoomIn} disabled={!pdfDocument || scale >= maxScale - 0.001} aria-label={t('timeline:pdf/zoom_in')} title={t('timeline:pdf/zoom_in')}>
              <Icons.Plus />
            </button>
            <button type="button" className={classNames('pdfButton', { active: zoom == null })} onClick={fit} disabled={!pdfDocument} aria-label={t('timeline:pdf/fit_width')} title={t('timeline:pdf/fit_width')}>
              <Icons.FitWidth />
            </button>
          </div>
        )}
        <div className="pdfActions">
          {downloadButton}
          {!isPhone && (
            <button type="button" className="pdfButton pdfClose" onClick={onClose} aria-label={t('timeline:pdf/close')} title={t('timeline:pdf/close')}>
              <Icons.Close />
            </button>
          )}
        </div>
        {loading && (
          <div className={classNames('pdfProgress', { indeterminate: progress == null })}>
            <span style={progress == null ? undefined : { width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}
      </div>

      <div className="pdfBody" onMouseDown={keepFocusOnPages}>
        <div className="pdfScroller" ref={scrollerRef} tabIndex={-1}>
          {failed ? (
            <div className="pdfError" role="alert">
              <Icons.Alert />
              <strong>{t(damaged ? 'timeline:pdf/damaged_title' : 'timeline:pdf/error_title')}</strong>
              <span>{t(damaged ? `timeline:pdf/damaged_text${doc.onDownload ? '_app' : ''}` : 'timeline:pdf/error_text')}</span>
              <div className="pdfErrorActions">
                {!damaged && <button type="button" className="pdfPill primary" onClick={retry}>{t('timeline:pdf/retry')}</button>}
                {doc.onDownload && <button type="button" className="pdfPill" onClick={doc.onDownload}>{t('timeline:pdf/share')}</button>}
                {!doc.onDownload && url && <a className="pdfPill" href={url} target="_blank" rel="noopener noreferrer">{t('timeline:pdf/download')}</a>}
              </div>
            </div>
          ) : (
            <div
              className={classNames('pdfPages', { loading })}
              ref={pagesRef}
              style={{
                padding: `${padding}px ${gutter}px`,
                // The home indicator's band, when no file switcher sits over it
                paddingBottom: isPhone && !multiple ? `calc(${padding}px + env(safe-area-inset-bottom))` : padding,
              }}
            >
              {pdfDocument && sizes ? layout.map((page, i) => (
                <PDFPage
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${doc.key}-${i}`}
                  pdfDocument={pdfDocument}
                  number={i + 1}
                  width={page.width}
                  height={page.height}
                  scale={scale}
                  active={i >= view.first && i <= view.last}
                  label={t('timeline:pdf/page_of', { page: i + 1, pages: pageCount })}
                />
              )) : (
                <div className="pdfPage skeleton" style={{ width: layout[0].width, height: layout[0].height }} />
              )}
            </div>
          )}
        </div>

        {multiple && !isPhone && (
          <React.Fragment>
            <button type="button" className="pdfDocNav prev" onClick={() => goTo(index - 1)} disabled={!prev} aria-label={t('timeline:pdf/previous_file')} title={prev ? prev.name : undefined}>
              <Icons.ChevronLeft size={28} />
            </button>
            <button type="button" className="pdfDocNav next" onClick={() => goTo(index + 1)} disabled={!next} aria-label={t('timeline:pdf/next_file')} title={next ? next.name : undefined}>
              <Icons.ChevronRight size={28} />
            </button>
          </React.Fragment>
        )}
      </div>

      {multiple && isPhone && (
        <div className="pdfDocBar" onMouseDown={keepFocusOnPages}>
          <button type="button" className="pdfButton" onClick={() => goTo(index - 1)} disabled={!prev}>
            <Icons.ChevronLeft />
            <span>{t('timeline:pdf/previous')}</span>
          </button>
          <span className="pdfDocCount">{fileText}</span>
          <button type="button" className="pdfButton" onClick={() => goTo(index + 1)} disabled={!next}>
            <span>{t('timeline:pdf/next')}</span>
            <Icons.ChevronRight />
          </button>
        </div>
      )}
    </Modal>
  );
};

export default PDFPreview;
